import { useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import DOMPurify from "dompurify";
import {
  FaCheck,
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimesCircle,
  FaDownload,
  FaExternalLinkAlt,
  FaTrash,
  FaEye,
} from "react-icons/fa";
import type { U2000Notification } from "../../types/U2000NotificationTypes";
import { useU2000NotificationContext } from "../../context/notification/U2000NotificationContext";

interface U2000NotificationItemProps {
  notification: U2000Notification;
}

/**
 * Composant affichant un item de notification individuel
 * Style Material Design avec icônes selon le type et l'importance
 * Optimisé avec React.memo pour éviter les re-renders inutiles dans les grandes listes
 */
const U2000NotificationItem = memo(({ notification }: U2000NotificationItemProps) => {
  const { t } = useTranslation("u2000");
  const { markAsRead, markAsUnread, deleteNotification } = useU2000NotificationContext();

  // Icône selon l'importance
  const importanceIcon = useMemo(() => {
    switch (notification.importance) {
      case "success":
        return <FaCheckCircle className="w-5 h-5 text-green-500" />;
      case "warning":
        return <FaExclamationTriangle className="w-5 h-5 text-yellow-500" />;
      case "error":
        return <FaTimesCircle className="w-5 h-5 text-red-500" />;
      case "critical":
        return <FaExclamationCircle className="w-5 h-5 text-red-600" />;
      case "info":
      default:
        return <FaInfoCircle className="w-5 h-5 text-blue-500" />;
    }
  }, [notification.importance]);

  // Formatage de la date
  const formattedDate = useMemo(() => {
    try {
      return formatDistanceToNow(new Date(notification.timestamp), {
        addSuffix: true,
        locale: fr,
      });
    } catch {
      return notification.timestamp;
    }
  }, [notification.timestamp]);

  // Vérifier si le fichier peut être ouvert dans le navigateur (extension dans l’URL ou fileMimeType envoyé par le backend)
  const downloadFilename = useMemo(() => {
    const id = notification.id;
    const mime = (notification.fileMimeType || "").toLowerCase();
    const jobType = (notification.metadata?.jobType as string) || "";
    const isExcel =
      mime.includes("spreadsheet") ||
      mime.includes("excel") ||
      mime.includes("openxmlformats-officedocument") ||
      jobType.includes("EXCEL_EXPORT");
    const isPdf = mime === "application/pdf" || jobType.includes("PDF_EXPORT");
    if (isExcel) return { name: `export-${id}`, ext: "xlsx" };
    if (isPdf) return { name: `rapport-${id}`, ext: "pdf" };
    const url = (notification.fileUrl || "").toLowerCase();
    if (url.endsWith(".xlsx")) return { name: `export-${id}`, ext: "xlsx" };
    return { name: `rapport-${id}`, ext: "pdf" };
  }, [notification.id, notification.fileUrl, notification.fileMimeType, notification.metadata?.jobType]);

  const canOpenInBrowser = useMemo(() => {
    if (!notification.fileUrl) return false;

    const url = notification.fileUrl.toLowerCase();
    const browserViewableExtensions = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".txt", ".html", ".htm"];
    if (browserViewableExtensions.some((ext) => url.endsWith(ext))) return true;

    const mime = (notification.fileMimeType || "").toLowerCase();
    const browserViewableMimeTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "image/webp",
      "image/svg+xml",
      "text/plain",
      "text/html",
    ];
    return browserViewableMimeTypes.some((mt) => mime === mt || mime.startsWith(mt + ";"));
  }, [notification.fileUrl, notification.fileMimeType]);

  // Sanitizer le message pour prévenir les attaques XSS si du HTML est présent
  // Note: React échappe automatiquement le contenu, mais DOMPurify ajoute une couche de sécurité supplémentaire
  const sanitizedMessage = useMemo(() => {
    // Si le message contient des balises HTML, on le sanitize
    if (notification.message.includes('<') || notification.message.includes('>')) {
      return DOMPurify.sanitize(notification.message, {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
      });
    }
    return notification.message;
  }, [notification.message]);

  // Gestion du clic sur la notification
  const handleClick = async () => {
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
        console.error("[Notification] Erreur lors du marquage comme lue:", error);
      }
    }
  };

  // Gestion du téléchargement : récupérer en Blob puis déclencher l’enregistrement (évite d’ouvrir le PDF dans le navigateur)
  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.fileUrl) return;
    const { name, ext } = downloadFilename;
    const filename = `${name}.${ext}`;
    try {
      const response = await fetch(notification.fileUrl, { credentials: "include" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn("[Notification] Téléchargement fetch échoué, fallback nouvel onglet:", err);
      }
      // Fallback : ouvrir dans un nouvel onglet pour ne pas quitter la page (le PDF s’ouvre ou se télécharge dans l’onglet)
      const link = document.createElement("a");
      link.href = notification.fileUrl;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Gestion de l'ouverture dans un nouvel onglet (lien explicite pour éviter blocage popup et garantir nouvel onglet)
  const handleOpenFileInNewTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.fileUrl) {
      const link = document.createElement("a");
      link.href = notification.fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Gestion de l'ouverture du lien
  const handleOpenLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.linkUrl) {
      window.open(notification.linkUrl, "_blank");
    }
  };

  // Gestion de la suppression
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteNotification(notification.id);
    } catch (error) {
      console.error("[Notification] Erreur lors de la suppression:", error);
    }
  };

  // Gestion du marquage lu/non lu
  const handleToggleRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (notification.isRead) {
        await markAsUnread(notification.id);
      } else {
        await markAsRead(notification.id);
      }
    } catch (error) {
      console.error("[Notification] Erreur lors du changement de statut:", error);
    }
  };

  return (
    <div
      className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-all duration-200 cursor-pointer relative ${
        !notification.isRead ? "bg-blue-50/50 border-l-4 border-l-blue-500" : ""
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        {/* Icône d'importance */}
        <div className="shrink-0 mt-1">{importanceIcon}</div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          {/* En-tête avec titre et date */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <h4 className={`text-sm font-semibold leading-tight ${!notification.isRead ? "text-gray-900" : "text-gray-700"}`}>
              {notification.title}
            </h4>
            <span className="text-xs text-gray-500 shrink-0 mt-0.5">{formattedDate}</span>
          </div>

          {/* Message */}
          <p 
            className={`text-sm leading-relaxed ${!notification.isRead ? "text-gray-800" : "text-gray-600"} mb-3`}
            dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
          />

          {/* Actions */}
          <div className="flex items-center justify-between gap-2">
            {/* Boutons à gauche - Télécharger, Ouvrir fichier et Ouvrir lien */}
            <div className="flex items-center gap-1">
              {/* Bouton télécharger */}
              {notification.fileUrl && (
                <button
                  onClick={handleDownload}
                  className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all duration-200"
                  aria-label={t("lblDownload")}
                  title={t("lblDownload")}
                >
                  <FaDownload className="w-4 h-4" />
                </button>
              )}

              {/* Bouton ouvrir fichier dans un nouvel onglet (seulement si le fichier peut être visualisé dans le navigateur) */}
              {notification.fileUrl && canOpenInBrowser && (
                <button
                  onClick={handleOpenFileInNewTab}
                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                  aria-label={t("lblOpenFileInNewTab") || "Ouvrir dans un nouvel onglet"}
                  title={t("lblOpenFileInNewTab") || "Ouvrir dans un nouvel onglet"}
                >
                  <FaEye className="w-4 h-4" />
                </button>
              )}

              {/* Bouton ouvrir lien */}
              {notification.linkUrl && (
                <button
                  onClick={handleOpenLink}
                  className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all duration-200"
                  aria-label={t("lblOpenLink")}
                  title={t("lblOpenLink")}
                >
                  <FaExternalLinkAlt className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Boutons à droite - Marquer lu/non lu et Supprimer */}
            <div className="flex items-center gap-1">
              {/* Bouton marquer lu/non lu */}
              <button
                onClick={handleToggleRead}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  notification.isRead
                    ? "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    : "text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                }`}
                aria-label={notification.isRead ? t("lblMarkAsUnread") : t("lblMarkAsRead")}
                title={notification.isRead ? t("lblMarkAsUnread") : t("lblMarkAsRead")}
              >
                <FaCheck className="w-4 h-4" />
              </button>

              {/* Bouton supprimer */}
              <button
                onClick={handleDelete}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                aria-label={t("btnDelete")}
                title={t("btnDelete")}
              >
                <FaTrash className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Nom d'affichage pour le debugging avec React DevTools
U2000NotificationItem.displayName = "U2000NotificationItem";

export default U2000NotificationItem;
